// Automatic recovery from failed dynamic imports ("Failed to fetch dynamically imported
// module: .../assets/index-XXXX.js") — DL-61837, reproduced live on test-o and in a local
// simulation (scratchpad/chunksim).
//
// The failure chain in production:
//   1. A chunk request misses (mid-deploy upload window, or a stale CDN edge) and the SPA
//      fallback answers it with 200 + index.html + `cache-control: public, max-age=14400`.
//   2. The browser HTTP cache — and, when it happens during service-worker install, the
//      workbox PRECACHE — now hold HTML under the chunk's URL for up to 4 hours (precache:
//      for the whole SW version), without ever revalidating against the healthy server.
//   3. Every subsequent load — fresh navigations included, and especially the reload the
//      "New version ready" prompt's OK button performs — fails the dynamic import and
//      react-router renders its raw "Unexpected Application Error!" page.
//
// Recovery (single-flight per page load, at most MAX_ATTEMPTS reloads per browsing
// session, budget restored after a boot that stays healthy for RESET_AFTER_MS):
//   a. Unregister service workers and purge CacheStorage FIRST — covers the
//      poisoned-precache variant, and guarantees the refetch in (b) reaches the network
//      instead of being answered by a controlling SW from a poisoned cache.
//   b. Re-fetch the failing asset URL(s) with `cache: 'reload'` — this bypasses AND
//      REPLACES the poisoned browser-HTTP-cache entry with the server's current response
//      (a plain location.reload() would NOT: subresources within max-age never revalidate).
//   c. Reload the page for a clean module graph.
//
// `vite:preloadError` is Vite's documented hook for exactly this class of failure — it
// fires for failed dynamic imports and failed module preloads compiled by Vite. A single
// broken boot fires it once per failing import expression, so several events arrive in a
// burst; the handler coalesces the burst into ONE recovery pass (the single-flight guard),
// otherwise concurrent passes race the attempts counter and the first reload aborts the
// others' in-flight cache-busting fetches.

import { nativeFetch } from 'lib/nativeNetwork';

const ATTEMPTS_KEY = 'delogue-chunk-recovery-attempts';
const MAX_ATTEMPTS = 2;
const RESET_AFTER_MS = 15_000;
// How long the single recovery pass waits to collect sibling failures from the same burst.
const BURST_WINDOW_MS = 250;

export const assetUrlsFrom = (message: string): string[] => {
  // "Failed to fetch dynamically imported module: https://host/assets/index-X.js"
  // "Unable to preload CSS for /assets/index-X.css"
  const matches = message.match(/(?:https?:\/\/[^\s'"]+)?\/assets\/[\w.-]+\.(?:m?js|css)/g);
  return [...new Set(matches ?? [])];
};

let recovering = false;
let anyFailureThisBoot = false;
const failedUrls = new Set<string>();

const recover = async (): Promise<void> => {
  // Let the rest of the failure burst land so every poisoned URL gets busted in one pass.
  await new Promise(resolve => window.setTimeout(resolve, BURST_WINDOW_MS));

  try {
    // (a) drop the service worker + every CacheStorage bucket (poisoned-precache variant;
    //     also ensures the refetch below hits the network, not a poisoned SW cache)
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.allSettled(registrations.map(r => r.unregister()));
    }
    if (window.caches) {
      const keys = await caches.keys();
      await Promise.allSettled(keys.map(k => caches.delete(k)));
    }
    // (b) bust the poisoned browser-HTTP-cache entries for the failing assets.
    //     MUST be nativeFetch: Mirage/Pretender replaces window.fetch at boot and rejects
    //     same-origin /assets/* URLs ("no route defined") without ever touching the
    //     network, so a plain fetch() here can never repair the HTTP cache.
    await Promise.allSettled(
      [...failedUrls].map(url => nativeFetch(url, { cache: 'reload', credentials: 'same-origin' }))
    );
  } catch {
    // best effort — the repair above is opportunistic; the reload below still helps
  }

  // The REPAIR above always runs — even once the reload budget is spent — so that when a
  // broken server heals later, a failing boot silently fixes the caches and the user's
  // next manual reload succeeds (instead of staying poisoned for the full max-age).
  const attempts = Number(sessionStorage.getItem(ATTEMPTS_KEY) ?? '0');
  if (attempts >= MAX_ATTEMPTS) {
    // A genuinely broken server/deploy: do not reload-loop; let the router error surface.
    recovering = false; // allow later failures in this boot to repair their URLs too
    return;
  }
  sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));
  // (c) clean module graph; the next boot re-registers a fresh SW that re-precaches
  window.location.reload();
};

export const installChunkErrorRecovery = (): void => {
  window.addEventListener('vite:preloadError', event => {
    anyFailureThisBoot = true;
    // event is Vite's typed VitePreloadErrorEvent (WindowEventMap augmentation in vite/client)
    const { payload } = event;
    const message = payload instanceof Error ? payload.message : String(payload ?? '');
    assetUrlsFrom(message).forEach(url => failedUrls.add(url));
    if (recovering) return; // coalesce the burst into one recovery pass
    recovering = true;
    void recover();
  });
  // A boot that stays healthy long enough restores the recovery budget, so the guard only
  // ever stops a genuine outage from looping — not the next incident days later.
  window.setTimeout(() => {
    if (!anyFailureThisBoot) sessionStorage.removeItem(ATTEMPTS_KEY);
  }, RESET_AFTER_MS);
};
